import React, { createContext, useContext, ReactNode } from 'react';

// API 응답 타입 정의
interface ProfileResponse {
  message: string;
  profile: {
    id: string;
    email: string;
    name: string;
    calledName: string;

    username?: string;
    enabled?: boolean;
    emailVerified?: boolean;
    phone_number?: string | null;
    phone_number_verified?: boolean;

    agree_terms_of_service?: boolean;
    agree_privacy_policy?: boolean;
    agree_marketing_information?: boolean;

    detail?: any;
  }
}

// 사용자 관리 API 응답 타입 정의
interface UserInfo {
  id: string;
  username: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  enabled: boolean;
  emailVerified: boolean;
  createdTimestamp?: number;
  attributes?: Record<string, any>;
}

// 대시보드 통계 관련 타입 정의
interface UserStatistics {
  total_users: number;
  active_users: number;
  inactive_users: number;
}

interface SubscriptionStatistics {
  total_subscription_orders: number;
  completed_subscription_orders: number;
  completed_subscription_amount: number;
}

interface PlanSubscriptionInfo {
  plan_name: string;
  subscriber_count: number;
  total_payment_amount: number;
  percentage: number;
}

interface PlanSubscriptionStatistics {
  total_paid_subscribers: number;
  plan_details: PlanSubscriptionInfo[];
}

interface PhysicalBookOrderStatistics {
  total_orders: number;
  delivery_state_counts: Record<string, number>;
}

interface DashboardStatistics {
  user_statistics: UserStatistics;
  subscription_statistics: SubscriptionStatistics;
  plan_subscription_statistics: PlanSubscriptionStatistics;
  physical_book_order_statistics: PhysicalBookOrderStatistics;
  last_updated: string;
}

interface PaginationInfo {
  current_page: number;
  page_size: number;
  total_records: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
  start_index: number;
  end_index: number;
}

interface PaginatedUserResponse {
  users: UserInfo[];
  pagination: PaginationInfo;
}

interface UserSearchParams {
  page?: number;
  page_size?: number;
  search?: string;
  social_provider?: string;
  enabled?: boolean;
  created_after?: string;
  created_before?: string;
  has_paid_membership?: boolean;
  subscription_type?: string;
  sort_by?: string[];  // 정렬 파라미터 추가
}

// API 컨텍스트 타입 정의
// 쿠폰 관련 타입 정의
interface CouponInfo {
  id: string;
  code: string;
  name: string;
  description?: string;
  discount_type: string;
  discount_value: number;
  min_order_amount?: number;
  max_discount_amount?: number;
  usage_limit?: number;
  used_count: number;
  status: string;
  coupon_type: string;
  valid_from: string;
  valid_until: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
  // 사용 내역 정보 추가
  last_used_at?: string;
  total_discount_amount: number;
}

interface CouponListResponse {
  coupons: CouponInfo[];
  total: number;
  page: number;
  size: number;
}

interface CouponStatsResponse {
  total_coupons: number;
  active_coupons: number;
  expired_coupons: number;
  used_coupons: number;
  total_discount_amount: number;
  usage_rate: number;
}

interface CouponCreateRequest {
  code: string;
  name: string;
  description?: string;
  discount_type: string;
  discount_value: number;
  min_order_amount?: number;
  max_discount_amount?: number;
  usage_limit?: number;
  coupon_type: string;
  valid_from: string;
  valid_until: string;
}

interface CouponUpdateRequest {
  name?: string;
  description?: string;
  discount_type?: string;
  discount_value?: number;
  min_order_amount?: number;
  max_discount_amount?: number;
  usage_limit?: number;
  status?: string;
  valid_from?: string;
  valid_until?: string;
}

interface DummyOrderGiftCouponRequest {
  product_id: string;
  count: number;
  valid_from?: string;  // ISO 8601 형식 날짜 문자열
  valid_until?: string; // ISO 8601 형식 날짜 문자열
}

interface DummyOrderGiftCouponResponse {
  success: boolean;
  message: string;
  order_id?: string;
  coupon_codes?: string[];
  error_code?: string;
}

interface ApiContextType {
  getProfile: () => Promise<ProfileResponse>;
  saveProfile: (profileData: { name?: string; calledName?: string }) => Promise<any>;
  saveProfileName: (name: string) => Promise<any>;
  saveProfileCalledName: (calledName: string) => Promise<any>;
  saveAgreeMarketingInformation: (agree: boolean) => Promise<any>;

  // 회원권 관련 API
  activateMembershipByCoupon: (code: string) => Promise<any>;
  withdrawMembership: () => Promise<any>;

  // 상품 관련 API
  getProducts: () => Promise<any>;

  // Order 관련 API
  issueOrder: (orderData: any) => Promise<any>;
  approvePayment: (paymentData: any) => Promise<any>;

  // MyPage 관련 API
  getMyHome: () => Promise<any>;
  
  // 사용자 관리 관련 API
  getUsers: (params: UserSearchParams) => Promise<PaginatedUserResponse>;
  getUserDetail: (userId: string) => Promise<UserInfo>;
  updateUser: (userId: string, userData: Partial<UserInfo>) => Promise<any>;
  toggleUserStatus: (userId: string, enabled: boolean) => Promise<any>;
  deleteUser: (userId: string) => Promise<any>;
  getUserGroups: (userId: string) => Promise<any[]>;
  addUserToGroup: (userId: string, groupId: string) => Promise<any>;
  removeUserFromGroup: (userId: string, groupId: string) => Promise<any>;
  
  // 사용자 홈 정보 조회 API
  getUserHomeInfo: (userId: string) => Promise<any>;
  
  // 주문 관리 관련 API
  cleanOrders: (deep_clean?: boolean) => Promise<any>;
  getOrders: (params: any) => Promise<any>;
  getOrderDetail: (orderId: string) => Promise<any>;
  updateOrder: (orderId: string, orderData: any) => Promise<any>;
  refundOrder: (orderId: string, refundData: any) => Promise<any>;
  deleteOrder: (orderId: string) => Promise<any>;


  // 실물책 주문 관련 API
  getBookCoverStyles: () => Promise<any>;
  getBookCoverStyle: (styleId: string) => Promise<any>;
  orderPhysicalBook: (orderData: any) => Promise<any>;
  deletePhysicalBookOrder: (projectId: number, bookId: number) => Promise<any>;
  downloadBookContents: (projectId: number, bookId: number, is_physical?: boolean) => Promise<Blob>;

  // 실물책 주문 및 배송 관리 관련 API
  getDeliveries: (params: any) => Promise<any>;
  getDeliveryDetail: (orderId: string) => Promise<any>;
  updateDeliveryStatus: (orderId: string, deliveryState: string, adminNote?: string) => Promise<any>;
  cancelDelivery: (orderId: string, adminNote?: string) => Promise<any>;
  getDeliveryStatistics: (startDate?: string, endDate?: string) => Promise<any>;
  getDeliveryStates: () => Promise<any>;
  
  // 대시보드 통계 관련 API
  getDashboardStatistics: () => Promise<DashboardStatistics>;
  getUserStatistics: () => Promise<UserStatistics>;
  getSubscriptionStatistics: () => Promise<SubscriptionStatistics>;
  getPlanSubscriptionStatistics: () => Promise<PlanSubscriptionStatistics>;
  getPhysicalBookOrderStatistics: () => Promise<PhysicalBookOrderStatistics>;
  
  // 인터뷰 관리 관련 API
  getInterviewDetail: (userId: string, projectId: string, episodeId: string) => Promise<any>;
  updateInterview: (userId: string, projectId: string, episodeId: string, interviewData: any) => Promise<any>;
  deleteInterview: (userId: string, projectId: string, episodeId: string) => Promise<any>;
  regenerateInterview: (userId: string, projectId: string, episodeId: string) => Promise<any>;
  deleteInterviewFile: (userId: string, projectId: string, episodeId: string, file_name: string) => Promise<any>;
  saveInterviewFile: (userId: string, projectId: string, episodeId: string, file_name: string, content: any) => Promise<any>;
  generateInterviewPDF: (projectId: number, episodeId: number, skipExisting?: boolean) => Promise<any>;

  // 전자책 관리 관련 API
  getDigitalBookInfo: (userId: string, projectId: string, bookId: string) => Promise<any>;
  deleteDigitalBook: (userId: string, projectId: string, bookId: string) => Promise<any>;
  rewriteDigitalBook: (userId: string, projectId: number) => Promise<any>;
  editDigitalBookSection: (userId: string, projectId: string, bookId: string, sectionId: number, sectionData: any) => Promise<any>;
  genDigitalBookPDF: (userId: string, projectId: string, bookId: string) => Promise<any>;
  
  // 카메라 영상 녹화 관련 API
  getCameraWebSocketInfo: (projectId: number, episodeId?: number) => Promise<any>;
  completeCameraRecording: (projectId: number, sessionId: string, episodeId?: number) => Promise<any>;
  deleteCameraRecording: (projectId: number, cameraId: string) => Promise<any>;
  getCameraRecordingWebSocketUrl: (projectId: number, episodeId?: number) => string;

  // Shared 관련 API
  getSharedContentURL: (type: string, code: string) => Promise<any>;
  
  // 게시판 관련 API
  createBoard: (boardData: any) => Promise<any>;
  getBoards: (params: any) => Promise<any>;
  getBoard: (boardId: number) => Promise<any>;
  updateBoard: (boardId: number, boardData: any) => Promise<any>;
  deleteBoard: (boardId: number) => Promise<any>;
  createReply: (boardId: number, replyData: any) => Promise<any>;
  updateReply: (replyId: number, replyData: any) => Promise<any>;
  deleteReply: (replyId: number) => Promise<any>;
  getBoardStatistics: () => Promise<any>;

  // 쿠폰 관리 관련 API
  getCoupons: (params: any) => Promise<CouponListResponse>;
  getCoupon: (couponId: string) => Promise<CouponInfo>;
  createCoupon: (couponData: CouponCreateRequest) => Promise<CouponInfo>;
  updateCoupon: (couponId: string, couponData: CouponUpdateRequest) => Promise<CouponInfo>;
  deleteCoupon: (couponId: string) => Promise<any>;
  getCouponStats: () => Promise<CouponStatsResponse>;
  cleanupExpiredCoupons: () => Promise<any>;
  getProductsForCoupon: () => Promise<any>;
  createDummyOrderAndIssueGiftCoupons: (request: DummyOrderGiftCouponRequest) => Promise<DummyOrderGiftCouponResponse>;
  getHeaders: () => HeadersInit;
}

// API 컨텍스트 생성
const ApiContext = createContext<ApiContextType | undefined>(undefined);

// API Provider Props 타입
interface ApiProviderProps {
  children: ReactNode;
}

// API Provider 컴포넌트
export const ApiProvider: React.FC<ApiProviderProps> = ({ children }) => {
  // API 베이스 URL 가져오기
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  if (!process.env.REACT_APP_API_BASE_URL) {
    console.warn('REACT_APP_API_BASE_URL 환경변수가 설정되지 않았습니다. 기본값을 사용합니다:', apiBaseUrl);
    console.warn('환경변수 파일(.env)을 생성하고 REACT_APP_API_BASE_URL=http://localhost:8000을 설정하세요.');
  }

  // API 호출을 위한 공통 헤더 설정
  const getHeaders = (): HeadersInit => {
    const token = localStorage.getItem('membership_access_token'); // 토큰이 있다면 가져오기
    
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  };
  const getHeadersForFormData = (): HeadersInit => {
    const token = localStorage.getItem('membership_access_token'); // 토큰이 있다면 가져오기
    
    return {
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  };

  // 공통 에러 처리 함수
  const handleApiError = async (response: Response) => {
    if (!response.ok) {
      console.error('API 에러 응답:', {
        status: response.status,
        statusText: response.statusText,
        url: response.url,
        headers: Object.fromEntries(response.headers.entries())
      });
      
      try {
        const errorData = await response.json();
        console.error('API 에러 데이터:', errorData);
        throw new Error(errorData.message || errorData.detail || `API 요청 실패: ${response.status} ${response.statusText}`);
      } catch (parseError) {
        console.error('API 에러 응답 파싱 실패:', parseError);
        throw new Error(`API 요청 실패: ${response.status} ${response.statusText}`);
      }
    }
    return response;
  };

  // ==================================================================================
  // 사용자 계정관련 API 함수들
  // ==================================================================================
  // 프로필 정보 가져오기
  const getProfile = async (): Promise<ProfileResponse> => {
    try {
      const response = await fetch(`${apiBaseUrl}/users/profile`, {
        method: 'GET',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('프로필 정보 가져오기 실패:', error);
      throw error;
    }
  };

  // 프로필 이름 저장하기
  const saveProfile = async (profileData: { name?: string; calledName?: string }): Promise<any> => {
    try {
      const body: { name?: string; calledName?: string } = {};
      if (profileData.name) body.name = profileData.name;
      if (profileData.calledName) body.calledName = profileData.calledName;

      const response = await fetch(`${apiBaseUrl}/users/profile`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(body),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('프로필 정보 저장 실패:', error);   
      throw error;
    }
  }
  // 프로필 이름 저장하기 (기존 이름 수정)
  const saveProfileName = async (name: string): Promise<any> => {
    try {
      const body = {
        name: name,
      };

      const response = await fetch(`${apiBaseUrl}/users/profile`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(body),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('프로필 이름 저장 실패:', error);
      throw error;
    }
  };

  // 프로필 호출 이름 저장하기
  const saveProfileCalledName = async (calledName: string): Promise<any> => {
    try {
      const body = {
        calledName: calledName,
      };

      const response = await fetch(`${apiBaseUrl}/users/profile`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(body),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('프로필 호출 이름 저장 실패:', error);
      throw error;
    }
  };

  // 마케팅 정보 동의 저장하기 (선택적)
  const saveAgreeMarketingInformation = async (agree: boolean): Promise<any> => {
    try {
      const body = {
        agree_marketing_information: agree,
      };

      const response = await fetch(`${apiBaseUrl}/users/profile`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(body),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('마케팅 정보 동의 저장 실패:', error);
      throw error;
    }
  }

  // ==================================================================================
  // 회원권 관련 API 함수들
  // ==================================================================================
  // 회원권 활성화 API
  const activateMembershipByCoupon = async (code: string): Promise<any> => {
    try {
      const body = {
        code: code,
      };

      const response = await fetch(`${apiBaseUrl}/users/membership/activate/coupon/code`, {
        method: 'POST',
        headers: getHeaders(),
          body: JSON.stringify(body),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('회원권 활성화 실패:', error);
      throw error;
    }
  }

  // 회원권 탈퇴 API
  const withdrawMembership = async (): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/users/membership/withdraw`, {
        method: 'POST',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('회원권 탈퇴 실패:', error);
      throw error;
    }
  }

  // ==================================================================================
  // 상품 관련 API 함수들
  // ==================================================================================
  // 상품 목록 조회 API
  const getProducts = async (retryCount = 0): Promise<any> => {
    try {
      // const token = localStorage.getItem('membership_access_token');
      
      // 토큰이 없으면 에러 발생
      // if (!token) {
      //   throw new Error('인증 토큰이 없습니다. 로그인이 필요합니다.');
      // }
      
      const url = `${apiBaseUrl}/store/products/plans`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(),
      });
      
      await handleApiError(response);
      const result = await response.json();
      return result;
    } catch (error) {
      // 네트워크 오류이고 재시도 횟수가 3회 미만인 경우 재시도
      if (error instanceof Error && 
          error.message.includes('Failed to fetch') && 
          retryCount < 3) {
        console.log(`네트워크 오류 발생. ${retryCount + 1}회 재시도 중...`);
        
        // 1초 대기 후 재시도
        await new Promise(resolve => setTimeout(resolve, 1000));
        return getProducts(retryCount + 1);
      }
      
      // 에러 타입 안전하게 처리
      if (error instanceof Error) {
        // 네트워크 에러인 경우 더 자세한 정보 제공
        if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        }
      }
      
      throw error;
    }
  }

  // ==================================================================================
  // Order 관련 API 함수들 
  // ==================================================================================
  // 주문 발행 API
  const issueOrder = async (orderData: any): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/store/order/issue`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(orderData),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('주문 발행 실패:', error);
      throw error;
    }
  };

  // 결제 승인 API
  const approvePayment = async (paymentData: any): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/store/order/approve`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(paymentData),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('결제 승인 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  }

  // MyHome 관련 API 함수
  const getMyHome = async (): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/home`, {
        method: 'GET',
        headers: getHeaders(),
      });

      await handleApiError(response);
      const result = await response.json();

      console.log('내 페이지 정보 조회 성공:', result);

      return result
    } catch (error) {
      console.error('내 페이지 정보 조회 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // ==================================================================================
  // 실물책 주문 관련 API 함수들
  // ==================================================================================
  // 실물책 커버 스타일 목록 조회 API
  const getBookCoverStyles = async (): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/cms/bookdeco/list`, {
        method: 'GET',
        headers: getHeaders(),
      });
      await handleApiError(response);
      return await response.json();
    }
    catch (error) {
      console.error('실물책 커버 스타일 목록 조회 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  }

  // 실물책 커버 스타일 조회 API
  const getBookCoverStyle = async (styleId: string): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/cms/bookdeco/${styleId}`, {
        method: 'GET',
        headers: getHeaders(),
      });
      await handleApiError(response);
      return await response.json();
    }
    catch (error) {
      console.error('실물책 커버 스타일 조회 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  }

  // 실물책 주문 요청 API
  const orderPhysicalBook = async (orderData: any): Promise<any> => {
    const {
      projectId,
      bookId,
      postalCode,
      address,
      addressDetail,
      recipientName,
      recipientPhone,
      image,
      coverStyleId,
    } = orderData;
  
    const formData = new FormData();
    formData.append('postal_code', postalCode);
    formData.append('address', address);
    formData.append('address_detail', addressDetail || '');
    formData.append('recipient_name', recipientName);
    formData.append('recipient_phone', recipientPhone);
    formData.append('cover_style_id', coverStyleId);

    if (image && typeof image === 'string' && image.startsWith('data:')) {
      const file = base64ToFile(image, 'upload.png');
      formData.append('image', file);
    } else if (image instanceof File) {
      formData.append('image', image);
    } else if (image instanceof Blob) {
      formData.append('image', image, 'upload.png');
    } else {
      throw new Error('이미지 파일이 올바르지 않습니다.');
    }
  
    const url = `${apiBaseUrl}/projects/${projectId}/books/${bookId}/physical/order/request`;
  
    const response = await fetch(url, {
      method: 'POST',
      headers: getHeadersForFormData(),
      body: formData,
    });
  
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || '주문 요청에 실패했습니다.');
    }
  
    return response.json();
  };

  // 실물책 주문 삭제 API
  const deletePhysicalBookOrder = async (projectId: number, bookId: number): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/projects/${projectId}/books/${bookId}/physical/order/delete`, {
        method: 'DELETE',
        headers: getHeaders(),
      });
      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('실물책 주문 삭제 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 실물책 컨텐츠 다운로드 API
  const downloadBookContents = async (projectId: number, bookId: number, is_physical: boolean = true): Promise<Blob> => {
    try {
      const url = is_physical ? `${apiBaseUrl}/projects/${projectId}/books/${bookId}/physical/contents/download` : 
        `${apiBaseUrl}/projects/${projectId}/books/${bookId}/contents/download`;
      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.blob();
    } catch (error) {
      console.error('실물책 컨텐츠 다운로드 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 전자책 정보 상세 조회 API
  const getDigitalBookInfo = async (userId: string, projectId: string, bookId: string): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/users/${userId}/projects/${projectId}/books/${bookId}`, {
        method: 'GET',
        headers: getHeaders(),
      });
  
      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('전자책 정보 상세 조회 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // base64 → File 변환 함수
  function base64ToFile(base64: string, filename: string): File {
    const arr = base64.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  }
  // ==================================================================================

  // ==================================================================================
  // 사용자 관리 관련 API 함수들
  // ==================================================================================
  // 사용자 목록 조회 (검색 및 페이지네이션)
  const getUsers = async (params: UserSearchParams): Promise<PaginatedUserResponse> => {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.page_size) queryParams.append('page_size', params.page_size.toString());
      if (params.search) queryParams.append('search', params.search);
      if (params.social_provider) queryParams.append('social_provider', params.social_provider);
      if (params.enabled !== undefined) queryParams.append('enabled', params.enabled.toString());
      if (params.created_after) queryParams.append('created_after', params.created_after);
      if (params.created_before) queryParams.append('created_before', params.created_before);
      if (params.has_paid_membership !== undefined) queryParams.append('has_paid_membership', params.has_paid_membership.toString());
      if (params.subscription_type) queryParams.append('subscription_type', params.subscription_type);
      
      // 정렬 파라미터 추가
      if (params.sort_by && params.sort_by.length > 0) {
        params.sort_by.forEach((sort: string) => {
          queryParams.append('sort_by', sort);
        });
      }

      const response = await fetch(`${apiBaseUrl}/admin/users?${queryParams.toString()}`, {
        method: 'GET',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('사용자 목록 조회 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 사용자 상세 정보 조회
  const getUserDetail = async (userId: string): Promise<UserInfo> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/users/${userId}`, {
        method: 'GET',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('사용자 상세 정보 조회 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 사용자 정보 수정
  const updateUser = async (userId: string, userData: Partial<UserInfo>): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/users/${userId}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(userData),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('사용자 정보 수정 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 사용자 계정 상태 변경
  const toggleUserStatus = async (userId: string, enabled: boolean): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/users/${userId}/status?enabled=${enabled}`, {
        method: 'PATCH',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('사용자 상태 변경 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 사용자 삭제
  const deleteUser = async (userId: string): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/users/${userId}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('사용자 삭제 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 사용자 그룹 조회
  const getUserGroups = async (userId: string): Promise<any[]> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/users/${userId}/groups`, {
        method: 'GET',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('사용자 그룹 조회 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 사용자 그룹 추가
  const addUserToGroup = async (userId: string, groupId: string): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/users/${userId}/groups/${groupId}`, {
        method: 'POST',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('사용자 그룹 추가 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 사용자 그룹 제거
  const removeUserFromGroup = async (userId: string, groupId: string): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/users/${userId}/groups/${groupId}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('사용자 그룹 제거 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };
  // ==================================================================================

  // 사용자 홈 정보 조회 API
  const getUserHomeInfo = async (userId: string): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/users/${userId}/home`, {
        method: 'GET',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('사용자 홈 정보 조회 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  const cleanOrders = async (deep_clean: boolean = false): Promise<any> => {
    try {
      const body = {
        deep_clean: deep_clean
      };

      const response = await fetch(`${apiBaseUrl}/admin/orders/clean`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(body),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('주문 목록 초기화 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 주문 관리 관련 API 함수들
  const getOrders = async (params: any): Promise<any> => {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.page_size) queryParams.append('page_size', params.page_size.toString());
      if (params.search) queryParams.append('search', params.search);
      if (params.payment_state) queryParams.append('payment_state', params.payment_state);
      if (params.is_gift !== undefined) queryParams.append('is_gift', params.is_gift.toString());
      if (params.min_amount) queryParams.append('min_amount', params.min_amount.toString());
      if (params.max_amount) queryParams.append('max_amount', params.max_amount.toString());
      if (params.created_after) queryParams.append('created_after', params.created_after);
      if (params.created_before) queryParams.append('created_before', params.created_before);
      
      // 정렬 파라미터 추가
      if (params.sort_by && params.sort_by.length > 0) {
        params.sort_by.forEach((sort: string) => {
          queryParams.append('sort_by', sort);
        });
      }

      const response = await fetch(`${apiBaseUrl}/admin/orders?${queryParams.toString()}`, {
        method: 'GET',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('주문 목록 조회 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  const getOrderDetail = async (orderId: string): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/orders/${orderId}`, {
        method: 'GET',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('주문 상세 정보 조회 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  const updateOrder = async (orderId: string, orderData: any): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/orders/${orderId}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(orderData),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('주문 정보 수정 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  const refundOrder = async (orderId: string, refundData: any): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/orders/${orderId}/refund`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(refundData),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('주문 환불 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };


  const deleteOrder = async (orderId: string): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/orders/${orderId}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });

      return await response.json();
    } catch (error) {
      console.error('주문 삭제 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // ==================================================================================
  // 실물책 주문 관리 관련 API 함수들
  // ==================================================================================
  const getDeliveries = async (params: any): Promise<any> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.page_size) queryParams.append('page_size', params.page_size.toString());
      if (params.search) queryParams.append('search', params.search);
      if (params.delivery_state) queryParams.append('delivery_state', params.delivery_state);
      if (params.created_after) queryParams.append('created_after', params.created_after);
      if (params.created_before) queryParams.append('created_before', params.created_before);
      if (params.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params.sort_order) queryParams.append('sort_order', params.sort_order);

      const response = await fetch(`${apiBaseUrl}/admin/deliveries?${queryParams.toString()}`, {
        method: 'GET',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('실물책 주문 목록 조회 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  const getDeliveryDetail = async (orderId: string): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/deliveries/${orderId}`, {
        method: 'GET',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('실물책 주문 상세 정보 조회 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  const updateDeliveryStatus = async (orderId: string, deliveryState: string, adminNote?: string): Promise<any> => {
    try {
      const body = {
        delivery_state: deliveryState,
        admin_note: adminNote,
      };

      const response = await fetch(`${apiBaseUrl}/admin/deliveries/${orderId}/status`, {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify(body),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('실물책 주문 상태 변경 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  const cancelDelivery = async (orderId: string, adminNote?: string): Promise<any> => {
    try {
      const body = {
        admin_note: adminNote,
      };

      const response = await fetch(`${apiBaseUrl}/admin/deliveries/${orderId}/cancel`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(body),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('실물책 주문 취소 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  const getDeliveryStatistics = async (startDate?: string, endDate?: string): Promise<any> => {
    try {
      const queryParams = new URLSearchParams();
      if (startDate) queryParams.append('start_date', startDate);
      if (endDate) queryParams.append('end_date', endDate);

      const response = await fetch(`${apiBaseUrl}/admin/deliveries/statistics?${queryParams.toString()}`, {
        method: 'GET',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('실물책 주문 통계 조회 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  const getDeliveryStates = async (): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/deliveries/states`, {
        method: 'GET',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('실물책 주문 상태 목록 조회 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  

  // ==================================================================================
  // 대시보드 통계 관련 API 함수들
  // ==================================================================================
  
  // 전체 대시보드 통계 조회
  const getDashboardStatistics = async (): Promise<DashboardStatistics> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/dashboard/statistics`, {
        method: 'GET',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('대시보드 통계 조회 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 사용자 현황 통계 조회
  const getUserStatistics = async (): Promise<UserStatistics> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/dashboard/users/statistics`, {
        method: 'GET',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('사용자 현황 통계 조회 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 구독 현황 통계 조회
  const getSubscriptionStatistics = async (): Promise<SubscriptionStatistics> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/dashboard/subscriptions/statistics`, {
        method: 'GET',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('구독 현황 통계 조회 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 플랜 구독 정보 통계 조회
  const getPlanSubscriptionStatistics = async (): Promise<PlanSubscriptionStatistics> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/dashboard/plans/statistics`, {
        method: 'GET',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('플랜 구독 정보 통계 조회 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 실물책 주문 현황 통계 조회
  const getPhysicalBookOrderStatistics = async (): Promise<PhysicalBookOrderStatistics> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/dashboard/physical-books/statistics`, {
        method: 'GET',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('실물책 주문 현황 통계 조회 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // ==================================================================================
  // 인터뷰 관리 관련 API 함수들
  // ==================================================================================
  // 인터뷰 상세 조회
  const getInterviewDetail = async (userId: string, projectId: string, episodeId: string): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/users/${userId}/projects/${projectId}/episodes/${episodeId}/interview`, {
        method: 'GET',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('인터뷰 상세 조회 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 인터뷰 수정
  const updateInterview = async (userId: string, projectId: string, episodeId: string, interviewData: any): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/users/${userId}/projects/${projectId}/episodes/${episodeId}/interview`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(interviewData),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('인터뷰 수정 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 인터뷰 삭제
  const deleteInterview = async (userId: string, projectId: string, episodeId: string): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/users/${userId}/projects/${projectId}/episodes/${episodeId}/interview`, {
        method: 'DELETE',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('인터뷰 삭제 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 인터뷰 다시 생성
  const regenerateInterview = async (userId: string, projectId: string, episodeId: string): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/users/${userId}/projects/${projectId}/episodes/${episodeId}/interview/regenerate`, {
        method: 'POST',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('인터뷰 삭제 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 인터뷰 파일 삭제
  const deleteInterviewFile = async (userId: string, projectId: string, episodeId: string, file_name: string): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/users/${userId}/projects/${projectId}/episodes/${episodeId}/interview/files/${file_name}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('인터뷰 파일 삭제 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 인터뷰 파일 저장
  const saveInterviewFile = async (userId: string, projectId: string, episodeId: string, file_name: string, content: any): Promise<any> => {
    const formData = new FormData();
    formData.append('content', content);

    const response = await fetch(`${apiBaseUrl}/admin/users/${userId}/projects/${projectId}/episodes/${episodeId}/interview/files/${file_name}`, {
      method: 'PUT',
      headers: getHeadersForFormData(),
      body: formData,
    });

    await handleApiError(response);
    return await response.json();
  };

  // 인터뷰 PDF 생성
  const generateInterviewPDF = async (projectId: number, episodeId: number, skipExisting: boolean = true): Promise<any> => {
    try {
      const queryParams = new URLSearchParams();
      if (skipExisting !== undefined) {
        queryParams.append('skip_existing', skipExisting.toString());
      }

      const response = await fetch(`${apiBaseUrl}/writer/generate/${projectId}/${episodeId}/pdf?${queryParams.toString()}`, {
        method: 'POST',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('인터뷰 PDF 생성 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 전자책 삭제
  const deleteDigitalBook = async (userId: string, projectId: string, bookId: string): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/users/${userId}/projects/${projectId}/books/${bookId}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });
  
      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('전자책 삭제 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 전자책 다시 쓰기
  const rewriteDigitalBook = async (userId: string, projectId: number): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/users/${userId}/projects/${projectId}/books/write`, {
        method: 'POST',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('전자책 다시 쓰기 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 전자책 섹션 수정
  const editDigitalBookSection = async (userId: string, projectId: string, bookId: string, sectionId: number, sectionData: any): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/users/${userId}/projects/${projectId}/books/${bookId}/sections/${sectionId}/edit`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(sectionData),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('전자책 섹션 수정 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 전자책 PDF 생성
  const genDigitalBookPDF = async (userId: string, projectId: string, bookId: string): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/users/${userId}/projects/${projectId}/books/${bookId}/pdf`, {
        method: 'POST',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('전자책 PDF 생성 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // ==================================================================================
  // 카메라 영상 녹화 관련 API 함수들
  // ==================================================================================
  // 카메라 WebSocket 연결 정보 조회
  const getCameraWebSocketInfo = async (projectId: number, episodeId?: number): Promise<any> => {
    try {
      const token = localStorage.getItem('membership_access_token');
      if (!token) {
        throw new Error('인증 토큰이 없습니다.');
      }
      
      // 프로젝트 레벨인 경우 episodeId 없이 요청
      const url = episodeId 
        ? `${apiBaseUrl}/projects/${projectId}/camera/stream/${episodeId}?token=${token}`
        : `${apiBaseUrl}/projects/${projectId}/camera/stream?token=${token}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('카메라 WebSocket 정보 조회 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 카메라 녹화 완료
  const completeCameraRecording = async (projectId: number, sessionId: string, episodeId?: number): Promise<any> => {
    try {
      const token = localStorage.getItem('membership_access_token');
      if (!token) {
        throw new Error('인증 토큰이 없습니다.');
      }
      
      // 프로젝트 레벨인 경우와 에피소드 레벨인 경우 다른 엔드포인트 사용
      const url = episodeId 
        ? `${apiBaseUrl}/projects/${projectId}/camera/stream/complete/${sessionId}/${episodeId}?token=${token}`
        : `${apiBaseUrl}/projects/${projectId}/camera/stream/complete/${sessionId}?token=${token}`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('카메라 녹화 완료 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 카메라 레코딩 삭제
  const deleteCameraRecording = async (projectId: number, cameraId: string): Promise<any> => {
    try {
      const token = localStorage.getItem('membership_access_token');
      if (!token) {
        throw new Error('인증 토큰이 없습니다.');
      }
      
      const url = `${apiBaseUrl}/projects/${projectId}/camera/${cameraId}?token=${token}`;
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('카메라 레코딩 삭제 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // WebSocket URL 생성
  const getCameraRecordingWebSocketUrl = (projectId: number, episodeId?: number): string => {
    const token = localStorage.getItem('membership_access_token');
    if (!token) {
      throw new Error('인증 토큰이 없습니다.');
    }

    // HTTP URL을 WebSocket URL로 변환
    const wsBaseUrl = apiBaseUrl.replace('http://', 'ws://').replace('https://', 'wss://');
    
    if (episodeId) {
      return `${wsBaseUrl}/projects/${projectId}/camera/stream/${episodeId}?token=${token}`;
    } else {
      return `${wsBaseUrl}/projects/${projectId}/camera/stream?token=${token}`;
    }
  };

  // Shared 관련 API
  const getSharedContentURL = async (type: string, code: string): Promise<any> => {
    try {
      const url = `${apiBaseUrl}/share/search?type=${type}&code=${code}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('Shared 관련 API 호출 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // ==================================================================================
  // 게시판 관련 API 함수들
  // ==================================================================================
  
  // 게시글 작성
  const createBoard = async (boardData: any): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/boards`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(boardData),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('게시글 작성 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 게시글 목록 조회
  const getBoards = async (params: any): Promise<any> => {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.page_size) queryParams.append('page_size', params.page_size.toString());
      if (params.category) queryParams.append('category', params.category);
      if (params.status) queryParams.append('status', params.status);
      if (params.search) queryParams.append('search', params.search);
      if (params.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params.sort_order) queryParams.append('sort_order', params.sort_order);

      const response = await fetch(`${apiBaseUrl}/boards?${queryParams.toString()}`, {
        method: 'GET',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('게시글 목록 조회 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 게시글 상세 조회
  const getBoard = async (boardId: number): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/boards/${boardId}`, {
        method: 'GET',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('게시글 상세 조회 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 게시글 수정
  const updateBoard = async (boardId: number, boardData: any): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/boards/${boardId}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(boardData),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('게시글 수정 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 게시글 삭제
  const deleteBoard = async (boardId: number): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/boards/${boardId}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('게시글 삭제 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 답변 작성
  const createReply = async (boardId: number, replyData: any): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/boards/${boardId}/replies`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(replyData),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('답변 작성 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 답변 수정
  const updateReply = async (replyId: number, replyData: any): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/replies/${replyId}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(replyData),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('답변 수정 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 답변 삭제
  const deleteReply = async (replyId: number): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/replies/${replyId}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('답변 삭제 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 게시글 통계 조회
  const getBoardStatistics = async (): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/boards/statistics`, {
        method: 'GET',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('게시글 통계 조회 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // ==================================================================================
  // 쿠폰 관리 관련 API 함수들
  // ==================================================================================
  
  // 쿠폰 목록 조회
  const getCoupons = async (params: any): Promise<CouponListResponse> => {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.size) queryParams.append('size', params.size.toString());
      if (params.status) queryParams.append('status', params.status);
      if (params.coupon_type) queryParams.append('coupon_type', params.coupon_type);
      if (params.search) queryParams.append('search', params.search);
      if (params.created_after) queryParams.append('created_after', params.created_after);
      if (params.created_before) queryParams.append('created_before', params.created_before);
      if (params.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params.sort_order) queryParams.append('sort_order', params.sort_order);

      const response = await fetch(`${apiBaseUrl}/admin/coupons?${queryParams.toString()}`, {
        method: 'GET',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('쿠폰 목록 조회 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 쿠폰 상세 조회
  const getCoupon = async (couponId: string): Promise<CouponInfo> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/coupons/${couponId}`, {
        method: 'GET',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('쿠폰 상세 조회 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 쿠폰 생성
  const createCoupon = async (couponData: CouponCreateRequest): Promise<CouponInfo> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/coupons`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(couponData),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('쿠폰 생성 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 쿠폰 수정
  const updateCoupon = async (couponId: string, couponData: CouponUpdateRequest): Promise<CouponInfo> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/coupons/${couponId}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(couponData),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('쿠폰 수정 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 쿠폰 삭제
  const deleteCoupon = async (couponId: string): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/coupons/${couponId}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('쿠폰 삭제 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 쿠폰 통계 조회
  const getCouponStats = async (): Promise<CouponStatsResponse> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/coupons/stats/overview`, {
        method: 'GET',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('쿠폰 통계 조회 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 만료된 쿠폰 정리
  const cleanupExpiredCoupons = async (): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/coupons/cleanup/expired`, {
        method: 'POST',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('만료된 쿠폰 정리 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };

  // 상품 목록 조회 (쿠폰 생성용)
  const getProductsForCoupon = async (): Promise<any> => {
    try {
      const response = await fetch(`${apiBaseUrl}/store/products/plans`, {
        method: 'GET',
        headers: getHeaders(),
      });

      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('상품 목록 조회 실패:', error);
      throw error;
    }
  };

  // 더미 오더 생성 및 선물용 쿠폰 발행
  const createDummyOrderAndIssueGiftCoupons = async (request: DummyOrderGiftCouponRequest): Promise<DummyOrderGiftCouponResponse> => {
    try {
      console.log('API 요청 데이터:', request);
      
      const response = await fetch(`${apiBaseUrl}/admin/coupons/create-gift-coupons`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(request),
      });

      console.log('API 응답 상태:', response.status);
      
      await handleApiError(response);
      return await response.json();
    } catch (error) {
      console.error('더미 오더 및 선물용 쿠폰 발행 실패:', error);
      if (error instanceof Error) {
        console.error('에러 메시지:', error.message);
      }
      throw error;
    }
  };


  // ==================================================================================
  // API 컨텍스트 값 정의
  // ==================================================================================
  const apiContextValue: ApiContextType = {
    // 사용자 계정관련 API 함수
    getProfile,
    saveProfile,
    saveProfileName,
    saveProfileCalledName,
    saveAgreeMarketingInformation,
    // 회원권 관련 API 함수
    activateMembershipByCoupon,
    withdrawMembership,
    // 상품 관련 API 함수
    getProducts,
    // Order 관련 API 함수
    issueOrder,
    approvePayment,
    // MyHome 관련 API 함수
    getMyHome,
    // 인터뷰 관리 관련 API 함수
    getInterviewDetail,
    updateInterview,
    deleteInterview,
    regenerateInterview,
    deleteInterviewFile,
    saveInterviewFile,
    generateInterviewPDF,
    // 사용자 관리 관련 API 함수
    getUsers,
    getUserDetail,
    updateUser,
    toggleUserStatus,
    deleteUser,
    getUserGroups,
    addUserToGroup,
    removeUserFromGroup,
    // 사용자 홈 정보 조회 API
    getUserHomeInfo,
    // 주문 관리 관련 API 함수
    cleanOrders,
    getOrders,
    getOrderDetail,
    updateOrder,
    refundOrder,
    deleteOrder,
    // 실물책 주문 관리 관련 API 함수
    getDeliveries,
    getDeliveryDetail,
    updateDeliveryStatus,
    cancelDelivery,
    getDeliveryStatistics,
    getDeliveryStates,
    // 실물책 관리 관련 API 함수
    getBookCoverStyles,
    getBookCoverStyle,
    orderPhysicalBook,
    deletePhysicalBookOrder,
    downloadBookContents,
    // 대시보드 통계 관련 API 함수
    getDashboardStatistics,
    getUserStatistics,
    getSubscriptionStatistics,
    getPlanSubscriptionStatistics,
    getPhysicalBookOrderStatistics,
    // 전자책 관리 관리 관련 API 함수
    getDigitalBookInfo,
    deleteDigitalBook,
    rewriteDigitalBook,
    editDigitalBookSection,
    genDigitalBookPDF,
    // 카메라 영상 녹화 관련 API 함수
    getCameraWebSocketInfo,
    completeCameraRecording,
    deleteCameraRecording,
    getCameraRecordingWebSocketUrl,
    // Shared 관련 API 함수
    getSharedContentURL,
    // 게시판 관련 API 함수
    createBoard,
    getBoards,
    getBoard,
    updateBoard,
    deleteBoard,
    createReply,
    updateReply,
    deleteReply,
    getBoardStatistics,
    // 쿠폰 관리 관련 API 함수
    getCoupons,
    getCoupon,
    createCoupon,
    updateCoupon,
    deleteCoupon,
    getCouponStats,
    cleanupExpiredCoupons,
    getProductsForCoupon,
    createDummyOrderAndIssueGiftCoupons,
    getHeaders,
  };

  return (
    <ApiContext.Provider value={apiContextValue}>
      {children}
    </ApiContext.Provider>
  );
};

// API 컨텍스트 사용을 위한 커스텀 훅
export const useApi = (): ApiContextType => {
  const context = useContext(ApiContext);
  if (context === undefined) {
    throw new Error('useApi는 ApiProvider 내부에서 사용되어야 합니다.');
  }
  return context;
};
